/**
 * Toggle favorite for a regulation (detail header / table actions).
 */

import React, { useState } from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import StarIcon from "@mui/icons-material/Star";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  StarBorderCustomIcon,
  regulationFavoriteIconButtonSx,
  regulationFavoriteIconSx,
} from "../RegulationsTable/regulationFavoriteIcons";
import {
  addRegulationFavorite,
  removeRegulationFavorite,
} from "../../services/regulationsService";
import { useSnackbar } from "../../contexts/SnackbarContext";

export type RegulationFavoriteButtonProps = Readonly<{
  regulationId: string;
  isFavorite: boolean;
  onFavoriteChange: (isFavorite: boolean) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}>;

export const RegulationFavoriteButton: React.FC<RegulationFavoriteButtonProps> = ({
  regulationId,
  isFavorite,
  onFavoriteChange,
  disabled = false,
  sx,
}) => {
  const { showMessage } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);

  const handleToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || submitting || !regulationId.trim()) return;

    const nextFavorite = !isFavorite;
    setSubmitting(true);
    try {
      if (nextFavorite) {
        await addRegulationFavorite(regulationId);
      } else {
        await removeRegulationFavorite(regulationId);
      }
      onFavoriteChange(nextFavorite);
      showMessage(
        nextFavorite ? "Added to favorites" : "Removed from favorites",
        "success",
      );
    } catch (err) {
      showMessage(
        err instanceof Error ? err.message : "Failed to update favorite",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const tooltipTitle = isFavorite ? "Remove from favorites" : "Add to favorites";
  const showTooltip = !(disabled || submitting);

  return (
    <Tooltip title={showTooltip ? tooltipTitle : ""} arrow>
      <IconButton
        size="small"
        disabled={disabled || submitting}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={handleToggle}
        aria-pressed={isFavorite}
        aria-label={tooltipTitle}
        sx={[
          regulationFavoriteIconButtonSx,
          { flexShrink: 0 },
          ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
        ]}
      >
        {submitting ? (
          <CircularProgress
            size={14}
            sx={{ color: "rgba(0, 0, 0, 0.54)" }}
          />
        ) : isFavorite ? (
          <StarIcon
            sx={{
              ...regulationFavoriteIconSx,
              color: "#f59e0b",
            }}
          />
        ) : (
          <StarBorderCustomIcon sx={regulationFavoriteIconSx} />
        )}
      </IconButton>
    </Tooltip>
  );
};
